import styled from "@emotion/styled";
import { CircularProgress, IconButton, InputAdornment } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { TextField } from "@shared/components/TextField";
import { useDebouncedCallback } from "use-debounce";
import { blurActiveElement } from "@shared/utils/blur-active-element";
import { useCallback, useState } from "react";

const FiltersContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  align-items: flex-end;
`;

type UserThreadsQueryParams = {
  threadIdFilter?: string;
};

type Props = {
  updateUserThreadsQueryParams: (params: UserThreadsQueryParams) => void;
  defaultValues: {
    threadIdFilter: string;
  };
  isLoading: boolean;
};

type FilterState = Omit<UserThreadsQueryParams, "limit" | "page">;

export const UserLogsFiltersNavbar = (props: Props) => {
  const { updateUserThreadsQueryParams, defaultValues, isLoading } = props;
  const [isSearching, setIsSearching] = useState(false);
  const [filterState, setFilterState] = useState<FilterState>({
    threadIdFilter: defaultValues.threadIdFilter,
  });

  const debouncedCallback = useDebouncedCallback(
    (value: string, name: string) => {
      updateUserThreadsQueryParams({
        [name]: value,
      });
      setIsSearching(false);
    },
    400
  );

  const handleFilterInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    const name = event.target.name;
    setFilterState((old) => ({ ...old, [name]: value }));
    setIsSearching(true);
    debouncedCallback(value, name);
  };

  const clearCurrentFilter = useCallback(() => {
    setFilterState({ threadIdFilter: "" });
    updateUserThreadsQueryParams({
      threadIdFilter: "",
    });
    blurActiveElement();
  }, [updateUserThreadsQueryParams]);

  return (
    <FiltersContainer>
      <TextField
        label="Thread ID"
        name="threadIdFilter"
        onChange={handleFilterInputChange}
        value={filterState.threadIdFilter ?? ""}
        hideHelperTextSpace
        InputProps={{
          endAdornment: (
            <InputAdornment position="start">
              {isSearching || isLoading ? (
                <IconButton size="small" disabled>
                  <CircularProgress size={20} />
                </IconButton>
              ) : (
                <IconButton size="small" onClick={clearCurrentFilter}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />
    </FiltersContainer>
  );
};
